import { ApolloClient, CombinedGraphQLErrors, InMemoryCache, concat, createHttpLink, from, type NormalizedCacheObject } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";

type CreateClientOptions = {
  uri: string;
  initialState?: NormalizedCacheObject;
};

export function createApolloClient({ uri, initialState }: CreateClientOptions) {
  const cache = new InMemoryCache({
    typePolicies: {
      // The API currently returns empty IDs for step ingredients, which causes
      // Apollo to collapse distinct items onto the same cache record.
      RecipeStepIngredient: {
        keyFields: false,
      },
    },
  });

  if (initialState) {
    cache.restore(initialState);
  }

  const httpLink = createHttpLink({
    uri,
    useGETForQueries: false,
    fetch,
  });

  const errorLink = onError(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      );
      return;
    }

    console.log(`[Network error]: ${error}`);
  });

  const retryLink = new RetryLink({
    attempts: async (_count, _operation, error) => (error as { statusCode?: number } | undefined)?.statusCode === 403,
  });

  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    cache,
    link: concat(retryLink, from([errorLink, httpLink])),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
      },
    },
  });
}

// Cache a single ApolloClient instance in the browser so navigations reuse the in-memory cache.
let browserClient: ApolloClient | null = null;
let browserClientUri: string | null = null;

function getBrowserClient(uri: string, initialState?: NormalizedCacheObject) {
  if (!browserClient || browserClientUri !== uri) {
    browserClient = createApolloClient({ uri, initialState });
    browserClientUri = uri;
    return browserClient;
  }

  if (initialState) {
    const existingCache = browserClient.extract() as NormalizedCacheObject;
    browserClient.cache.restore({
      ...existingCache,
      ...initialState,
    });
  }

  return browserClient;
}

export default function gqlClientConnect(uri: string, initialState?: NormalizedCacheObject) {
  if (typeof window === "undefined") {
    return createApolloClient({ uri, initialState });
  }

  return getBrowserClient(uri, initialState);
}
